/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Application logger: console output plus Rollbar reporting (skipped on
 * localhost unless ROLLBAR_ENABLED_ON_LOCALHOST). All Rollbar messages get
 * the shared (gitSha)(networkId)(userId) prefix.
 */
import {BrowserLocationService} from '../../app/angular-platform-providers';
import {Injectable, Injector} from '@angular/core';
import * as _ from 'lodash';

import BUILD_INFO from '../../gen-config/build-info';
import {ApiConfig} from '../../app/config/api-config';
import {LocalDebugInfo} from '../session/local-debug-info';
import {LOCAL_NETWORK_PROFILE, LOCAL_USER_PROFILE} from '../session/session-profile.tokens';
import {Session} from '../session/session';
import {AdnRollbarService, ROLLBAR_ENABLED_ON_LOCALHOST} from './adn-rollbar.service';

/* eslint-disable no-console */

export type AdnLogRollbarLevel = 'debug' | 'info' | 'warning' | 'error';

// Keys that must never reach Rollbar — callers routinely log raw $http
// request/response objects and session data at varying nesting depths.
const SENSITIVE_FIELD_KEYS = new Set([
  'authorization',
  'authtoken',
  'accesstoken',
  'refreshtoken'
]);

export function omitSensitiveRollbarFields(value: any): any {
  if (!_.isObject(value)) {
    return value;
  }

  const scrubbed = _.cloneDeep(value);
  const seen = new WeakSet<object>();
  const scrub = (candidate: any): void => {
    if (!_.isObject(candidate) || seen.has(candidate)) {
      return;
    }
    seen.add(candidate);
    _.forEach(candidate, (_fieldValue, fieldName) => {
      const normalizedName = String(fieldName).replace(/[_-]/g, '').toLowerCase();
      if (SENSITIVE_FIELD_KEYS.has(normalizedName)) {
        delete candidate[fieldName];
        return;
      }
      scrub(candidate[fieldName]);
    });
  };
  scrub(scrubbed);
  return scrubbed;
}

@Injectable({
  providedIn: 'root'
})
export class AdnLogService {
  constructor(
    private readonly adnRollbar: AdnRollbarService,
    // Profiles / location may not be ready during earliest Angular ErrorHandler
    // construction, and LocalNetworkProfile depends on this logger — resolve lazily
    // via cycle-safe tokens (not class imports) with LocalNetworkProfile /
    // LocalUserProfile / LocalUserPermissions.
    private readonly injector: Injector,
    private readonly apiConfig: ApiConfig
  ) {
  }

  log(...args: any[]): void {
    console.log(...args);
    this.report('info', args);
  }

  info(...args: any[]): void {
    console.info(...args);
    this.report('info', args);
  }

  warn(...args: any[]): void {
    console.warn(...args);
    this.report('warning', args);
  }

  error(...args: any[]): void {
    console.error(...args);
    this.report('error', args);
  }

  debug(...args: any[]): void {
    console.debug(...args);
    this.report('debug', args);
  }

  /** Console only — use when Rollbar is reported separately (or not at all). */
  errorLocal(...args: any[]): void {
    console.error(...args);
  }

  /**
   * Send an already-prepared message to Rollbar with optional trailing args
   * (Error instance, custom payload, etc.). Enriches and skips localhost
   * unless ROLLBAR_ENABLED_ON_LOCALHOST. Does not write to the console.
   */
  rollbar(level: AdnLogRollbarLevel, message: string, ...args: any[]): void {
    if (this.isLocalhost() && !ROLLBAR_ENABLED_ON_LOCALHOST) {
      return;
    }

    if (!this.adnRollbar || !_.isFunction(this.adnRollbar[level])) {
      return;
    }
    const enriched = this.enrichForRollbar(message);
    const rollbarArgs = _.filter(_.map(args, (arg) => this.safeValue(arg)), (arg) => !_.isNil(arg));
    try {
      if (rollbarArgs.length) {
        this.adnRollbar[level](enriched, ...rollbarArgs);
      } else {
        this.adnRollbar[level](enriched);
      }
    } catch (e) {
      // Never let logging break the app.
    }
  }

  private report(level: AdnLogRollbarLevel, args: any[]): void {
    const first = args[0];
    const extras = _.filter(_.map(args.slice(1), (arg) => this.safeValue(arg)), (arg) => !_.isNil(arg));
    const error = this.asRollbarError(first);

    // Pass a real Error through so Rollbar can capture frames. Sending only the
    // message string (historical behaviour) drops the stack entirely.
    if (error) {
      const message = error.message || String(error);
      if (extras.length) {
        this.rollbar(level, message, error, {extra: extras});
      } else {
        this.rollbar(level, message, error);
      }
      return;
    }

    const message = this.toMessage(first);
    if (extras.length) {
      this.rollbar(level, message, {extra: extras});
    } else {
      this.rollbar(level, message);
    }
  }

  private asRollbarError(value: any): Error | null {
    if (_.isNil(value)) {
      return null;
    }

    const candidates = [value.originalError, value.rejection, value.cause, value];
    for (let i = 0; i < candidates.length; i++) {
      const candidate = candidates[i];
      if (_.isError(candidate)) {
        return candidate;
      }
      if (candidate && _.isString(candidate.stack) && (_.isString(candidate.message) || _.isString(candidate.name))) {
        const err = new Error(candidate.message || candidate.name || 'Unknown error');
        err.name = candidate.name || err.name;
        err.stack = candidate.stack;
        return err;
      }
    }
    return null;
  }

  private isLocalhost(): boolean {
    if (this.apiConfig.getEnv() === 'localhost') {
      return true;
    }
    try {
      return this.injector.get(BrowserLocationService).hostname === 'localhost';
    } catch (e) {
      return false;
    }
  }

  /**
   * (gitSha)(networkId)(userId) message - (gitSha)(signIn)(expired)
   * Short SHA is in the prefix so Rollbar message truncation still shows it.
   */
  private enrichForRollbar(message: string): string {
    let networkId = '';
    let userId = '';
    let username = '';
    try {
      // Cycle-safe tokens only — importing LocalUserProfile / LocalNetworkProfile
      // here creates an ES-module cycle (profile → permissions → network → this logger).
      const networkProfile = this.injector.get(LOCAL_NETWORK_PROFILE);
      const localUserProfile = this.injector.get(LOCAL_USER_PROFILE);
      networkId = networkProfile.getNetworkId() || '';
      username = localUserProfile.get('username') || '';
      userId = localUserProfile.get('userId') || '';
    } catch (e) {
      // Profiles may not be ready during early bootstrap.
    }

    const shortSha = (BUILD_INFO.gitSha || 'unknown').substring(0, 6);
    const sessionUsername = Session.obtainData().username;
    let enriched = '(' + shortSha + ')(' + networkId + ')(' + userId + ') ' + message;
    if (username === sessionUsername && username === LocalDebugInfo.obtain().getInfo('username')) {
      enriched += ' - (' + shortSha + ')(' + LocalDebugInfo.obtain().getInfo('signIn') + ')(' + LocalDebugInfo.obtain().getInfo('expired') + ')';
    } else {
      enriched += ' - (' + sessionUsername + ')(' + shortSha + ')(' + LocalDebugInfo.obtain().getInfo('userId') + ')(' + LocalDebugInfo.obtain().getInfo('signIn') + ')(' + LocalDebugInfo.obtain().getInfo('expired') + ')';
    }
    return enriched;
  }

  private toMessage(value: any): string {
    if (_.isString(value)) {
      return value;
    }
    if (value instanceof Error) {
      return value.message || String(value);
    }
    if (_.isNil(value)) {
      return String(value);
    }
    try {
      return JSON.stringify(omitSensitiveRollbarFields(value));
    } catch (e) {
      return String(value);
    }
  }

  private safeValue(value: any): any {
    if (_.isError(value) || _.isString(value) || _.isNumber(value) || _.isBoolean(value) || _.isNil(value)) {
      return value;
    }
    try {
      return omitSensitiveRollbarFields(value);
    } catch (e) {
      return String(value);
    }
  }
}
